<template>
    <div id="scheduled-messages">
        <base-table
            :columns="columns"
            :items="flashPlanned"
            :loading="loading"
            :empty-text="$t('no-messages-in-section')"
            fill
            :fill-offset="88"
        >
            <template #cell(content)="{ item }">
                {{ item.attributes.content }}
            </template>
            <template #cell(audiences)="{ item }">
                <span v-if="item.attributes.audiences === null || item.attributes.audiences.length === 0">{{ $t('all') }}</span>
                <template v-else>
                    <div
                        v-for="phoneNumber in item.attributes.audiences"
                        :key="phoneNumber"
                        class="d-inline-flex p-0 mr-2 tags flash"
                    >
                        <p :title="phoneNumber">{{ recipientLabel(item.attributes, phoneNumber) }}</p>
                    </div>
                </template>
            </template>
            <template #cell(planned)="{ item }">
                <template v-if="item.attributes.plannedDate || item.attributes.plannedTime">
                    <div class="cell-datetime">
                        {{ fromUtc(`${item.attributes.plannedDate} ${item.attributes.plannedTime}`).date }}
                        {{ fromUtc(`${item.attributes.plannedDate} ${item.attributes.plannedTime}`).time }}
                        <timezone></timezone>
                    </div>
                </template>
                <span v-else>---</span>
            </template>
            <template #cell(actions)="{ item }">
                <button type="button" class="base-table-action" :aria-label="$t('send-now')" @click="sendFlashMessage(item.id)">
                    <font-awesome-icon class="text-dark-pink" :icon="['fas', 'paper-plane']"/>
                </button>
                <button type="button" class="base-table-action" :aria-label="$t('edit')" @click="editFlashMessage(item)">
                    <font-awesome-icon :icon="['fas', 'pencil-alt']"/>
                </button>
                <button type="button" class="base-table-action base-table-action-danger" :aria-label="$t('delete')" @click="deleteFlashMessage(item.id)">
                    <font-awesome-icon :icon="['fas', 'times']" />
                </button>
            </template>
        </base-table>
        <b-pagination
            v-if="flashScheduledMeta.page && flashScheduledMeta.page.total > flashScheduledMeta.page['per-page']"
            v-model="page.number"
            :total-rows="flashScheduledMeta.page.total"
            :per-page="flashScheduledMeta.page['per-page']"
            @input="loadFlashMessages"
            align="center"
        ></b-pagination>
        <send-modal
            :editMessage="editMessage"
            @flashListUpdated="loadFlashMessages()"
        >
        </send-modal>
    </div>
</template>

<script>

// Internal repository
import SendModal            from './SendModal.vue';
import Alert                from '@/helpers/Alert.js';
import BaseTable            from '@/core/components/BaseTable.vue';
import Timezone             from '@/core/components/Timezone.vue';
import Format               from '@/helpers/Format';
import RecipientNamesMixin  from '@/core/mixins/RecipientNamesMixin';

// Import external libraries
import { mapActions, mapState } from 'vuex';

export default {
    mixins: [RecipientNamesMixin],

    components: {
        SendModal,
        BaseTable,
        Timezone
    },
    data () {
        const FLASH_CATEGORY = 'flash';

        return {
            FLASH_CATEGORY,
            flashPlanned: [],
            loading:      false,
            editMessage:  false,
            page:         {
                number: 1,
                size:   25
            },
        };
    },

    computed: {
        ...mapState('core/scheduled-messages', {
            'flashMessagesPlanned': 'plannedScheduledMessages',
            'flashScheduledMeta':   'plannedScheduledMeta'
        }),

        columns () {
            const plural = 2;

            return [
                {
                    key:     'content',
                    label:   'Messages',
                    thStyle: { width: '38%' },
                    tdClass: 'cell-ellipsis'
                },
                {
                    key:     'audiences',
                    label:   this.$tc('recipient', plural),
                    thStyle: { width: '30%' }
                },
                {
                    key:     'planned',
                    label:   this.$t('schedule-diffusion'),
                    thStyle: { width: '22%' }
                },
                {
                    key:     'actions',
                    label:   '',
                    thStyle: { width: '120px' },
                    tdClass: 'cell-actions'
                }
            ];
        }
    },

    mounted () {
        this.loadFlashMessages();

        this.addFlashMessage();
    },

    methods: {
        ...mapActions('core/scheduled-messages', {
            delete:                    'delete',
            indexPlanned:              'indexPlanned',
            indexSent:                 'indexSent',
            selectFlashMessage:        'select',
            send:                      'send',
            resetSelectedFlashMessage: 'resetSelectedScheduledMessage'
        }),

        /**
         * Helper method to format date to use in template
         */
        fromUtc: Format.fromUtc,

        /**
         * Load planned on no_planned flash messages
         */
        loadFlashMessages () {
            this.loading = true;

            // Get list of flash planned messages
            this.indexPlanned({
                'category': this.FLASH_CATEGORY,
                page:       this.page
            })
                .then(() => {
                    this.flashPlanned = [];
                    Object.values(this.flashMessagesPlanned).forEach(message => {
                        message.attributes.contacts = [];
                        this.flashPlanned.push(message);
                    });
                })
                .finally(() => {
                    // Finish loading
                    this.loading = false;
                });

            // Get list of flash sent messages
            this.indexSent({
                'category': this.FLASH_CATEGORY,
                page:       this.page
            });
        },

        /**
         * Add flash message
         */
        addFlashMessage () {
            this.resetSelectedFlashMessage();
            this.editMessage = false;
            this.$bvModal.show('flash-send-sidebar');
        },

        /**
         * Edit a selected flash message
         */
        editFlashMessage (flashMessage) {
            this.editMessage = true;
            this.selectFlashMessage({ id: flashMessage.id, status: 'planned' })
                .then(() => {
                    this.$bvModal.show('flash-send-sidebar');
                });
        },

        /**
         * Send immediately a flash message
         */
        sendFlashMessage (id) {
            this.$dialog.confirm({
                title:       this.$t('title-flash-message-send'),
                text:        this.$t('confirm-scheduled-send'),
                centerText:  true,
                image:       '/images/confirm-send-scheduled-message.png',
                confirmText: this.$t('send-scheduled-message'),
                onConfirm:   () => this.send({ id })
            })
                .then((confirmed) => {
                    if (confirmed) {
                        this.loadFlashMessages();
                        Alert.success(this.$i18n.t('scheduled-send-success'));
                    }
                })
                .catch(() => {
                    Alert.fail(this.$i18n.t('default-error'));
                });
        },

        /**
         * Delete a selected flash message
         */
        deleteFlashMessage (id) {
            this.$dialog.confirm({
                title:       this.$t('title-flash-message-delete'),
                text:        this.$t('confirm-flash-message-delete'),
                centerText:  true,
                confirmText: this.$t('delete'),
                danger:      true,
                onConfirm:   () => this.delete({ id })
            })
                .then((confirmed) => {
                    if (confirmed) {
                        this.loadFlashMessages();
                        Alert.success(this.$i18n.t('scheduled-deleted-success'));
                    }
                })
                .catch(() => {
                    Alert.fail(this.$i18n.t('default-error'));
                });
        }
    }
};
</script>
